import type {
  AccountStatus,
  AgentConfig,
  Attachment,
  CreateBlueprintRequest,
  CreateSnapshotRequest,
  UpdateSnapshotRequest,
  ServerStatus,
  User,
  ListAgentDraftsResponse,
  UpsertAgentDraftRequest,
  UpsertAgentDraftResponse,
  AgentView,
  AgentViewsResponse,
  AuthSettings,
  AuthAllowlistMode,
  DevboxStatusResponse,
} from './types';
import type {
  CustomSandboxSize,
  EnqueueMessageRequest,
  ReorderQueueRequest,
  UpdateQueuedMessageRequest,
} from '@runloop/reflex-contract';
import type {
  UpdateBaseImageOverridesRequest,
  IntegrationSecretStatus,
  SecretManagerStatus,
  SecretRotationResult,
  ScopedSecretsListResponse,
  CreateScopedSecretRequest,
  ScopedSecret,
  UpdateScopedSecretRequest,
  UserCredentialsListResponse,
  ClaudeMaxCredential,
  CodexSubscriptionCredential,
  CredentialStatusResponse,
  ModelProviderSecret,
  ModelProviderSecretsListResponse,
  ModelProviderSecretType,
  CreateModelProviderSecretRequest,
  UpdateModelProviderSecretRequest,
  LlmTaskId,
  UpsertLlmTaskAssignmentRequest,
  AgentGroupResponse,
  ArchiveAllAgentsRequest,
  AgentLabelResponse,
  AgentMark,
  CreateAgentGroupRequest,
  CreateAgentLabelRequest,
  ListAgentGroupsResponse,
  ListAgentLabelsResponse,
  OrgOnboardingProfile,
  ResourceDependentsResponse,
  AdminOrganizationBillingEntity,
  AdminOrganizationBillingEntityMoveResult,
  BillingEntityDomainClaim,
  EmailVerificationRequestStatus,
} from '@reflex/shared';
import { buildContentBlocks } from '@runloop/reflex-contract';
import {
  ApiError,
  IS_CROSS_ORIGIN,
  authHeaders,
  clearAuthToken,
  getActiveOrganizationId,
  getActiveOrgPreference,
  getActiveOrgSlugFromUrl,
  getApiUrl,
  getAuthToken,
  markSessionEnded,
  orgHeaders,
  request,
  setActiveOrganizationId,
  setAuthToken,
  takeSessionEndedSignal,
} from '@reflex/ui/client/http';
import * as generatedActivity from '@reflex/ui/client/generated/activity';
import * as generatedAgentDrafts from '@reflex/ui/client/generated/agent-drafts';
import * as generatedAgentGroups from '@reflex/ui/client/generated/agent-groups';
import * as generatedAgentLabels from '@reflex/ui/client/generated/agent-labels';
import * as generatedAgents from '@reflex/ui/client/generated/agents';
import * as generatedAuth from '@reflex/ui/client/generated/auth';
import * as generatedBlueprints from '@reflex/ui/client/generated/blueprints';
import * as generatedConfig from '@reflex/ui/client/generated/config';
import * as generatedFlags from '@reflex/ui/client/generated/flags';
import * as generatedInvites from '@reflex/ui/client/generated/invites';
import * as generatedMcp from '@reflex/ui/client/generated/mcp';
import * as generatedMe from '@reflex/ui/client/generated/me';
import * as generatedOrganizations from '@reflex/ui/client/generated/organizations';
import * as generatedPermissions from '@reflex/ui/client/generated/permissions';
import * as generatedPlugins from '@reflex/ui/client/generated/plugins';
import * as generatedProfile from '@reflex/ui/client/generated/profile';
import * as generatedSecrets from '@reflex/ui/client/generated/secrets';
import * as generatedSnapshots from '@reflex/ui/client/generated/snapshots';
import * as generatedStatus from '@reflex/ui/client/generated/status';
import * as generatedTeams from '@reflex/ui/client/generated/teams';
import * as generatedUser from '@reflex/ui/client/generated/user';
import * as generatedUsers from '@reflex/ui/client/generated/users';
import * as generatedAdmin from '@reflex/ui/client/generated-internal/admin';
import type {
  PluginManifest,
  PluginHealthResponse,
  PluginHealthCheckEntry,
} from '@reflex/plugin-api';
import type {
  OrganizationMembership,
  OrganizationSubscriptionTier,
  OrganizationInvite,
  CreateOrganizationInviteResponse,
  OrganizationInviteListFilter,
  InviteLandingResponse,
  AcceptInviteResponse,
  AcceptPendingInvitesResponse,
  ListPendingInvitesResponse,
  UserOnboardingStateView,
  PluginCatalogResponse,
  OrgPluginListResponse,
  InstallationView,
  OrgPluginUninstallPreview,
  PluginServiceConfigValues,
  OrgPluginSettingsValues,
  OrgPluginSettingsView,
  MaintenanceState,
  ServiceSettings,
} from '@reflex/shared';
import { isSiteRelativeHref } from './site-relative-href';

const RETURN_PATH_KEY = 'auth_return_path';

export {
  ApiError,
  IS_CROSS_ORIGIN,
  authHeaders,
  clearAuthToken,
  getActiveOrganizationId,
  getActiveOrgPreference,
  getActiveOrgSlugFromUrl,
  getApiUrl,
  getAuthToken,
  markSessionEnded,
  orgHeaders,
  setActiveOrganizationId,
  takeSessionEndedSignal,
};
/**
 * One-shot signal captured from `#signin=new|existing` on the OAuth success
 * redirect. Consumed by the AuthedShell to render an account-created toast
 * when the flow minted a brand-new account.
 * Lives in sessionStorage so it survives the `replaceState` strip of the
 * hash but doesn't leak across browser sessions.
 */
const SIGNIN_SIGNAL_KEY = 'auth_signin_signal';

export interface SigninSignal {
  signin: 'new' | 'existing';
}

function isSigninFlag(v: string | null): v is 'new' | 'existing' {
  return v === 'new' || v === 'existing';
}

export function takeSigninSignal(): SigninSignal | null {
  try {
    const raw = sessionStorage.getItem(SIGNIN_SIGNAL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(SIGNIN_SIGNAL_KEY);
    const parsed = JSON.parse(raw) as Partial<SigninSignal>;
    if (parsed.signin && isSigninFlag(parsed.signin)) {
      return { signin: parsed.signin };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * A return path is only safe to restore after OAuth if it is a same-origin
 * absolute path. Reject protocol-relative (`//evil.com`) and absolute URLs so
 * an attacker can't craft a `?returnTo=` that turns sign-in into an open
 * redirect off our domain.
 */
function isSafeReturnPath(path: string | null | undefined): path is string {
  return !!path && isSiteRelativeHref(path);
}

export function saveReturnPath(): void {
  try {
    // An explicit `?returnTo=` wins over the current location. Surfaces that
    // detour through `/login` (e.g. the docs header "Sign in" link) thread the
    // page the visitor came from so the OAuth callback can send them back
    // there instead of stranding them on the auth page or the app root.
    const explicit = new URLSearchParams(window.location.search).get('returnTo');
    const candidate = isSafeReturnPath(explicit)
      ? explicit
      : window.location.pathname + window.location.search;
    if (candidate && candidate !== '/' && !isAuthPath(candidate)) {
      sessionStorage.setItem(RETURN_PATH_KEY, candidate);
    }
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Where to land after a sign-in that completes without leaving the SPA
 * (email/password, dev login). The login screen renders in place of the
 * page that lost its session, so the address bar still names that page;
 * honour it (or an explicit `?returnTo=`) the same way the OAuth callback
 * does, and fall back to the app root.
 */
export function postSignInPath(): string {
  saveReturnPath();
  const returnPath = getAndClearReturnPath();
  // Re-validate on read, not just on write: this value feeds
  // `window.location.assign`, so a stored path must be site-relative (no
  // scheme, no protocol-relative `//host`) or it is an open redirect.
  return isSafeReturnPath(returnPath) && !isAuthPath(returnPath) ? returnPath : '/';
}

export function getAndClearReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return path;
  } catch {
    return null;
  }
}

/**
 * Drops any saved return path without consuming it as a navigation target.
 *
 * Used when a saved path has served its purpose and would mis-route a later
 * navigation if left behind — e.g. the invite landing page saves its own
 * URL so the post-OAuth callback can return there, but once the invite is
 * accepted that URL is a consumed dead-end. Clearing it here keeps a later
 * `getAndClearReturnPath()` (e.g. in the onboarding "Continue" handler) from
 * bouncing the user back to it.
 */
export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Auth landing routes that render the "already signed in" card once the
 * visitor is authenticated. Returning to one of these right after OAuth
 * would strand the user on a dead-end screen instead of their workspace.
 */
const AUTH_PATHS = new Set(['/login', '/signup']);

function isAuthPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return AUTH_PATHS.has(pathname);
}

/**
 * Move a sign-in token out of the URL fragment and into storage, then strip
 * the fragment from the address bar.
 *
 * Runs at module load, and `main.tsx` calls it again explicitly before
 * `Sentry.init`: the token must be gone from `location.href` before any
 * third-party telemetry (Sentry breadcrumbs, replay Meta frames, PostHog
 * `$current_url`) can read it. Idempotent — a second call finds no
 * fragment and returns.
 */
export function captureAuthToken(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (!hash.includes('auth_token=')) return;

  const fragment = new URLSearchParams(hash.slice(1));
  const token = fragment.get('auth_token');
  // Removing the credential is mandatory; storage only improves the OAuth
  // landing experience and must never be allowed to block this operation.
  const landingPath = window.location.pathname + window.location.search;
  try {
    window.history.replaceState(null, '', landingPath);
  } catch {
    // A broken History API is the one browser failure we cannot remediate.
    return;
  }

  if (!token) return;
  try {
    setAuthToken(token);
  } catch {
    // Token storage unavailable; the URL is already safe.
  }

  const signin = fragment.get('signin');
  if (isSigninFlag(signin)) {
    try {
      sessionStorage.setItem(SIGNIN_SIGNAL_KEY, JSON.stringify({ signin }));
    } catch {
      // The welcome toast is optional.
    }
  }

  // The visitor just signed in; a notice about the previous session ending
  // would be stale by the time anything could show it.
  takeSessionEndedSignal();

  const returnPath = getAndClearReturnPath();
  const resolved = returnPath || landingPath;
  // A user who just completed OAuth must never land back on an auth page.
  const path = isAuthPath(resolved) ? '/' : resolved;
  try {
    window.history.replaceState(null, '', path);
  } catch {
    // The credential was removed by the first replacement above.
  }
}

captureAuthToken();

/**
 * Preserve the compatibility facade's established return type while an
 * operation is delegated to its generated fetcher. The generated OpenAPI
 * response is the source of truth; this adapter can disappear with the facade
 * once all call sites import generated operations directly.
 */
function generatedResult<T>(result: Promise<unknown>): Promise<T> {
  return result as Promise<T>;
}

/**
 * Coerce an OpenAPI list response to `T[]` at the API boundary.
 *
 * `generatedResult` above is an untyped cast (`result as Promise<T>`) with no
 * runtime validation, so a partial/retried response or a stale react-query
 * cache slot can land a truthy non-array (e.g. `{}` or `false`) where a list
 * is expected. A consumer's `.find` / `.map` / `for…of` then throws
 * `TypeError: (value ?? []) is not iterable` or `… is not a function` — the
 * Sentry `AGENT-FLOW-WEB-4X` Sharing-page crash, where `api.listTeams`
 * resolved to a truthy non-iterable and `for (const team of orgTeams ?? [])`
 * took down the whole page. PR #3478 guarded that one consumer; this coerces
 * the list endpoints themselves so every consumer (Sharing, ApiKeysStage,
 * OrganizationTeamsPage, AdminOrganizationDetailPage, useUsers) sees an array
 * regardless of upstream drift — "parse at the boundary; never probe data
 * YOLO-style." A consumer's existing `?? []` guard stays as defence in depth;
 * this makes the boundary the invariant.
 */
function coerceArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

export type AuthProviders = Record<
  string,
  {
    label: string;
    requiresStoredToken?: boolean;
    /**
     * `'oauth'` (default) renders a redirect sign-in button; `'password'`
     * (the email-auth plugin) renders an inline email/password form.
     */
    kind?: 'oauth' | 'password';
    /**
     * Whether an operator hid this provider from the login / registration
     * screens (the plugin's `hideOnLogin` admin toggle). Hidden providers are
     * still sent to the client so visitors who opt in via `?showHidden=true`
     * can reveal them.
     */
    hidden?: boolean;
  }
>;

// AuthMembership is the server-authoritative shape from @reflex/shared's
// OrganizationMembership schema. Re-exported here for backwards compatibility
// with existing imports in the web package.
export type AuthMembership = OrganizationMembership;

export interface AuthStatus {
  authenticated: boolean;
  authEnabled: boolean;
  user: User | null;
  providers?: AuthProviders;
  memberships?: AuthMembership[];
  activeOrganizationId?: string | null;
  /**
   * List of org IDs the user has membership on but where
   * `organizations.bootstrappedAt is null` AND the user holds
   * `org:manage`. Drives the AuthGate's per-org phase 2 wizard
   * redirect.
   */
  orgsNeedingBootstrap?: string[];
  isSuperAdmin?: boolean;
  /**
   * True when the user holds the built-in `platform-admin` role on their
   * `'system' sentinel` membership. Granted via `/admin/app-admins` and
   * treated by the server as super-admin-equivalent for permission
   * bypass. Use `isPlatformAdmin` (derived `isSuperAdmin || isAppAdmin`)
   * for UI gating instead of consulting this directly.
   */
  isAppAdmin?: boolean;
  /**
   * True when the first-run setup wizard has not yet been completed. The
   * web shell routes the user into `SetupPage` and prevents the rest of
   * the app from mounting.
   */
  setupRequired?: boolean;
  /**
   * Mirror of the server's NODE_ENV !== 'production' flag. The setup
   * wizard skips the admission-token step in development.
   */
  isProduction?: boolean;
  /**
   * Mirror of the platform-wide `allowSelfServeOrgCreation` auth setting.
   * `false` means only a platform admin can provision an org, so the
   * create-org surfaces say so instead of offering a form the server will
   * reject. Absent (older server) is treated as `true`.
   */
  allowSelfServeOrgCreation?: boolean;
  ssoRequirements?: Array<{
    organizationId: string;
    organizationSlug: string;
    organizationName: string;
    satisfied: boolean;
  }>;
  /**
   * Present on unauthenticated responses only when the server has
   * `/auth/dev-login` mounted (never in production). `users` lists the
   * identities the route will honor — the login screen renders a one-click
   * dev sign-in button per entry — and `autoLoginEmail`, when set, is the
   * identity the login screen signs in as automatically on load (seeded
   * previews skip the login screen entirely). Server-driven on purpose: the
   * built SPA has Vite's compile-time DEV flag off, so a client-side gate
   * would hide the affordance on seeded preview servers where dev-login is
   * available.
   */
  devLogin?: DevLoginStatus;
}

/** See {@link AuthStatus.devLogin}. */
export interface DevLoginStatus {
  users: string[];
  autoLoginEmail?: string;
}

/**
 * sessionStorage key set on explicit sign-out. While present, the login
 * screen ignores `devLogin.autoLoginEmail` — otherwise a seeded preview
 * would sign the user straight back in and they could never switch to
 * another advertised identity.
 */
export const DEV_LOGIN_AUTO_SUPPRESS_KEY = 'reflex.devLoginAutoSuppressed';

/**
 * Resolve the caller's auth status.
 *
 * Rejects when `/auth/me` could not be reached or answered with something
 * unusable. That is deliberately *not* flattened into an unauthenticated
 * status: "the server says you are signed out" and "I could not ask" are
 * different answers, and conflating them signed users out of an open tab
 * on any transient network blip (a sleeping laptop, a server restart).
 * Callers decide what an unreachable server means for them.
 *
 * The rejection distinguishes the two: an `ApiError` means the server
 * answered and the answer was unusable (a 500, or a 401 whose body is not
 * the structured `AuthStatus` this route normally returns), which is not
 * transient. Anything else is a transport failure. `useAuthProvider` branches
 * on exactly that, and must keep doing so — the SDK's global 401 handler
 * skips `/auth/*` (`ui/src/client/http.ts` `onUnauthorized`), so nothing
 * else will sign the user out if this call is the one that noticed.
 */
export async function checkAuth(): Promise<AuthStatus> {
  try {
    return await generatedResult<AuthStatus>(generatedAuth.getAuthStatus());
  } catch (error) {
    // `/auth/me` returns the unauthenticated AuthStatus as a structured 401
    // body because the login screen still needs its provider configuration.
    if (
      error instanceof ApiError &&
      typeof error.body.authenticated === 'boolean' &&
      typeof error.body.authEnabled === 'boolean'
    ) {
      return error.body as unknown as AuthStatus;
    }
    throw error;
  }
}

export const api = {
  /**
   * `statuses` is the one param the wire shape differs on: the route takes a
   * comma-separated list, because the UI's status facet is a *group* of
   * statuses ("stopped" covers stopping/stopped/terminated) and that grouping
   * is the client's to define. An empty array is passed through as an empty
   * value rather than dropped, so "a group that expands to nothing" keeps
   * matching nothing instead of silently un-filtering the list.
   */
  listAgents: (opts?: {
    userId?: string;
    cursor?: string;
    limit?: number;
    search?: string;
    pinned?: boolean;
    archived?: boolean;
    statuses?: readonly string[];
    mark?: string;
    groupId?: string;
    labelId?: string;
  }) => {
    const { statuses, ...rest } = opts ?? {};
    const params = { ...rest, ...(statuses ? { status: statuses.join(',') } : {}) };
    return generatedAgents.listAgents(params as Parameters<typeof generatedAgents.listAgents>[0]);
  },
  /**
   * List users. By default the server returns the active org's members for
   * regular users and every user globally for platform admins. Pass
   * `{ scope: 'org' }` to force org-scoping for everyone — required by
   * per-org member pickers (e.g. the agents-list Owner filter) so admins
   * don't see members from other organizations. Coerced to `User[]` at the
   * boundary (see {@link coerceArray}) so a drifted non-array can't reach a
   * consumer's `.find` / `.map` and crash the page.
   */
  listUsers: (opts?: { scope?: 'org' }) =>
    generatedUsers.listUsers(opts).then((r) => coerceArray(r)),
  // Super-admin only: enumerate every user in the system, paginated.
  // Backed by `GET /admin/users`, which 403s for non-platform-admins.
  // Defaults mirror the server (`page: 1`, `pageSize: 20`).
  listAllUsers: (opts: { page?: number; pageSize?: number } = {}) =>
    generatedAdmin.listAdminUsers(opts),
  getUser: (id: string) => generatedUsers.getUser(id),
  /**
   * List every user who currently holds the assignable platform-admin
   * role. Server-side gated on `admin:manage` (platform admin bypass).
   */
  listAppAdmins: () => generatedAdmin.listAppAdmins(),
  /**
   * Grant the platform-admin role to a user. Idempotent — calling
   * twice is a no-op. The server auto-provisions a SYSTEM_ORG
   * membership for the user as needed.
   */
  grantAppAdmin: (userId: string) => generatedAdmin.grantAppAdmin({ userId }),
  /**
   * Revoke the platform-admin role from a user. Idempotent. The server
   * refuses to let a non-super-admin self-demote (returns 400) to
   * prevent locking the operator out of the admin surface in a fresh
   * tenant.
   */
  revokeAppAdmin: (userId: string) => generatedAdmin.revokeAppAdmin(userId),
  /**
   * Set a user's lifecycle `status`. The server returns 400 when the
   * caller tries to disable themselves or another platform admin.
   * Gated on `admin:manage` (platform admins).
   */
  setUserStatus: (userId: string, status: AccountStatus) =>
    generatedAdmin.setAdminUserStatus(userId, { status }),
  /**
   * Set an organization's lifecycle `status`. When `'disabled'`, every
   * request whose active org resolves to this org is rejected with
   * HTTP 403 `organization_disabled` and members see the
   * `OrganizationDisabledScreen`. Gated on `admin:manage`.
   */
  setOrganizationStatus: (organizationId: string, status: AccountStatus) =>
    generatedAdmin.setAdminOrganizationStatus(organizationId, { status }),
  /**
   * Set, extend, or clear an organization's subscription. A non-null
   * `subscription` (re)opens the window to `now + durationDays` (default 14)
   * with the given `type` (default `'trial'`); `null` clears it so the org
   * has unlimited access. Access is computed from the window — once it
   * passes, the org's `disabledReason` resolves to `'subscription_expired'`
   * and members see the `TrialExpiredScreen`. Gated on platform-admin.
   * Returns the refreshed organization.
   */
  setOrganizationSubscription: (
    organizationId: string,
    subscription: { type?: 'trial'; durationDays?: number } | null,
  ) => generatedAdmin.setOrganizationSubscription(organizationId, { subscription }),
  /**
   * End a subscription immediately instead of waiting for its window to
   * elapse. The org's `disabledReason` flips to `'subscription_expired'` and
   * its members see the `TrialExpiredScreen` at once. Gated on
   * platform-admin. Returns the refreshed organization.
   */
  expireOrganizationSubscription: (organizationId: string) =>
    generatedAdmin.expireOrganizationSubscription(organizationId),
  /**
   * Hard-delete an organization and every row that references it (core
   * + plugin tables). Irreversible. Gated on platform-admin.
   */
  deleteOrganization: (organizationId: string) =>
    generatedAdmin.deleteAdminOrganization(organizationId),
  getAgent: (id: string) => generatedAgents.getAgent(id),
  /** The current user's per-agent last-viewed timestamps (unseen-changes dot). */
  listAgentViews: () => generatedResult<AgentViewsResponse>(generatedAgents.listAgentViews()),
  /** Record that the current user has opened this agent. */
  recordAgentView: (id: string) => generatedResult<AgentView>(generatedAgents.recordAgentView(id)),
  /** Put this agent back in the unread state for the current user. */
  markAgentUnread: (id: string) => generatedResult<AgentView>(generatedAgents.markAgentUnread(id)),
  /**
   * Live Runloop devbox status for an agent. `status` is the raw Runloop
   * status string (`running`, `suspended`, `resuming`, …), `null` when the
   * agent has no devbox yet, or `'unknown'` when the lookup couldn't resolve.
   * `resourceSize` is the box's live size (or `null`), preferred over the size
   * persisted on the agent row because it reflects the box as it actually runs.
   */
  getDevboxStatus: (id: string) =>
    generatedResult<DevboxStatusResponse>(generatedAgents.getAgentDevboxStatus(id)),
  getStream: (id: string) => generatedAgents.getAgentStream(id),
  /**
   * Opt into the server's paged stream: the initial turn-aligned window,
   * or (with `before`) the previous page. Unindexed agents answer with
   * the legacy full array — callers discriminate on the shape.
   */
  getStreamPaged: (id: string, before?: number) =>
    generatedAgents.getAgentStream(id, { paged: 'true', ...(before !== undefined && { before }) }),
  /**
   * The exact inclusive sequence window `[fromSeq, toSeq]` — the
   * continuous-scroll turn prefetch. Same shape discrimination as
   * `getStreamPaged`: a legacy full array means the server left paged mode.
   */
  getStreamRange: (id: string, fromSeq: number, toSeq: number) =>
    generatedAgents.getAgentStream(id, { fromSeq, toSeq }),
  getSessionIndex: (id: string) => generatedAgents.getAgentSessionIndex(id),
  getDevboxLogs: (id: string) =>
    generatedResult<{ content: string }>(generatedAgents.getAgentDevboxLogs(id)).then(
      (r) => r.content,
    ),
  /**
   * Live status of every service on the agent's devbox (launch steps and
   * runtime-registered daemons): the persisted lifecycle status plus a pid
   * liveness probe, so the UI can tell whether a registered daemon is
   * actually still running. Returns an empty list when the devbox is
   * unreachable or gone.
   */
  getServiceStatuses: (id: string) => generatedAgents.getAgentServiceStatuses(id),
  getServiceLog: async (id: string, serviceId: string) => {
    try {
      return await generatedResult<string>(generatedAgents.getAgentServiceLogs(id, serviceId));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      throw new Error(
        `Failed to fetch service log (serviceId=${serviceId}): ${error.status}${error.message ? ` ${error.message}` : ''}`,
        { cause: error },
      );
    }
  },
  startAgent: (config: AgentConfig) => generatedAgents.createAgent(config),
  listAgentDrafts: () =>
    generatedResult<ListAgentDraftsResponse>(generatedAgentDrafts.listAgentDrafts()),
  upsertAgentDraft: (body: UpsertAgentDraftRequest) =>
    generatedResult<UpsertAgentDraftResponse>(generatedAgentDrafts.saveAgentDraft(body)),
  deleteAgentDraft: (id: string) => generatedAgentDrafts.deleteAgentDraft(id),
  /** The org's agent groups, each with its current member count. */
  listAgentGroups: () =>
    generatedResult<ListAgentGroupsResponse>(generatedAgentGroups.listAgentGroups()),
  createAgentGroup: (body: CreateAgentGroupRequest) =>
    generatedResult<AgentGroupResponse>(generatedAgentGroups.createAgentGroup(body)),
  renameAgentGroup: (groupId: string, name: string) =>
    generatedResult<AgentGroupResponse>(generatedAgentGroups.updateAgentGroup(groupId, { name })),
  deleteAgentGroup: (groupId: string) => generatedAgentGroups.deleteAgentGroup(groupId),
  addAgentsToGroup: (groupId: string, agentIds: string[]) =>
    generatedResult<AgentGroupResponse>(
      generatedAgentGroups.addAgentGroupMembers(groupId, { agentIds }),
    ),
  removeAgentsFromGroup: (groupId: string, agentIds: string[]) =>
    generatedResult<AgentGroupResponse>(
      generatedAgentGroups.removeAgentGroupMembers(groupId, { agentIds }),
    ),
  /** The org's label vocabulary. Listing also seeds the built-in set. */
  listAgentLabels: () =>
    generatedResult<ListAgentLabelsResponse>(generatedAgentLabels.listAgentLabels()),
  createAgentLabel: (body: CreateAgentLabelRequest) =>
    generatedResult<AgentLabelResponse>(generatedAgentLabels.createAgentLabel(body)),
  renameAgentLabel: (labelId: string, name: string) =>
    generatedResult<AgentLabelResponse>(generatedAgentLabels.updateAgentLabel(labelId, { name })),
  deleteAgentLabel: (labelId: string) => generatedAgentLabels.deleteAgentLabel(labelId),
  applyAgentLabel: (labelId: string, agentIds: string[]) =>
    generatedResult<AgentLabelResponse>(
      generatedAgentLabels.addAgentLabelMembers(labelId, { agentIds }),
    ),
  removeAgentLabel: (labelId: string, agentIds: string[]) =>
    generatedResult<AgentLabelResponse>(
      generatedAgentLabels.removeAgentLabelMembers(labelId, { agentIds }),
    ),
  updateAgent: (id: string, patch: { name?: string; pinned?: boolean }) =>
    generatedAgents.updateAgent(id, patch),
  switchAgentSubscription: (id: string, providerSecretId: string) =>
    generatedAgents.switchAgentSubscription(id, { providerSecretId }),
  updateAgentRuntimeSettings: (id: string, patch: { model?: string }) =>
    generatedAgents.updateAgentRuntimeSettings(id, patch),
  attachAgentMcpServers: (id: string, mcpServerIds: string[]) =>
    // One request resolves credentials, runs the registration script, and
    // activates the servers; the client default cut it off mid-work.
    generatedAgents.attachAgentMcpServers(id, { mcpServerIds }, { timeoutMs: 90_000 }),
  interruptAgent: (id: string) => generatedAgents.interruptAgent(id),
  sendControlResponse: (id: string, payload: unknown) =>
    generatedAgents.sendAgentControlResponse(id, { payload }),
  stopAgent: (id: string) => generatedAgents.stopAgent(id),
  completeAgent: (id: string) => generatedAgents.completeAgent(id),
  killAgent: (id: string) => generatedAgents.killAgent(id),
  archiveAgent: (id: string) => generatedAgents.archiveAgent(id),
  archiveAllAgents: (body: ArchiveAllAgentsRequest = {}) => generatedAgents.archiveAllAgents(body),
  unarchiveAgent: (id: string) => generatedAgents.unarchiveAgent(id),
  pinAgent: (id: string) => generatedAgents.pinAgent(id),
  unpinAgent: (id: string) => generatedAgents.unpinAgent(id),
  setAgentMark: (id: string, mark: AgentMark | null) => generatedAgents.setAgentMark(id, { mark }),
  sendMessage: (id: string, message: string, attachments?: Attachment[]) => {
    const hasInlineAttachment = attachments?.some(
      (a) => (a.type === 'image' || a.type === 'file') && a.data,
    );
    return generatedAgents.sendAgentMessage(
      id,
      hasInlineAttachment ? { content: buildContentBlocks(message, attachments) } : { message },
    );
  },

  // Delivery status of a mailbox command (the `commandId` a 202 send
  // returned). `waitMs` holds the request open server-side until the
  // command settles, capped by the server.
  getAgentCommandStatus: (id: string, commandId: string, waitMs?: number, signal?: AbortSignal) =>
    generatedAgents.getAgentCommandStatus(
      id,
      commandId,
      waitMs ? { wait: waitMs } : undefined,
      // The server may hold the request for the full `waitMs`; give the
      // transport headroom past that so a held read is never cut short.
      { ...(signal ? { signal } : {}), timeoutMs: (waitMs ?? 0) + 10_000 },
    ),

  // Server-owned message queue (drained by the agent runner whenever the
  // agent transitions to an accepts-input state).
  getAgentQueue: (id: string) => generatedAgents.getAgentQueue(id),
  enqueueAgentMessage: (id: string, body: EnqueueMessageRequest) =>
    generatedAgents.enqueueAgentMessage(id, body),
  updateQueuedMessage: (id: string, msgId: string, body: UpdateQueuedMessageRequest) =>
    generatedAgents.updateQueuedAgentMessage(id, msgId, body),
  deleteQueuedMessage: (id: string, msgId: string) =>
    generatedAgents.removeQueuedAgentMessage(id, msgId),
  reorderAgentQueue: (id: string, body: ReorderQueueRequest) =>
    generatedAgents.reorderAgentQueue(id, body),

  getStatus: () => generatedResult<ServerStatus>(generatedStatus.getStatus()),
  // Admin-only — secret-presence + operational config. Returns 401/403 for
  // non-admins (audit M4). Callers should gate on `isSuperAdmin`/admin:read.
  getAdminStatus: () => generatedAdmin.getAdminStatus(),
  getWebhookActivity: () => generatedAdmin.getAdminWebhookActivity(),
  getConfig: () => generatedConfig.getConfig(),
  // Admin-only — read/write platform maintenance mode. Gated on `admin:read` /
  // `admin:manage`; a successful write is also pushed to every client over the
  // WebSocket so open sessions update their banner without a reload.
  getMaintenance: () => generatedAdmin.getMaintenance(),
  setMaintenance: (state: MaintenanceState) => generatedAdmin.setMaintenance(state),
  getServiceSettings: () => generatedAdmin.getServiceSettings(),
  updateServiceSettings: (settings: ServiceSettings) =>
    generatedAdmin.updateServiceSettings(settings),
  getEmailSettings: () => generatedAdmin.getEmailSettings(),
  getPlugins: (organizationId?: string | null) =>
    generatedResult<Array<PluginManifest>>(generatedConfig.listPluginManifests({ organizationId })),
  getAgentModelSupport: () => generatedConfig.getAgentModelSupport(),
  getPluginHealth: (name: string) =>
    generatedResult<PluginHealthResponse>(generatedPlugins.getPluginHealth(name)),
  runPluginDependencyCheck: (name: string) =>
    generatedResult<PluginHealthCheckEntry>(
      generatedPlugins.refreshPluginHealthDependencies(name, { timeoutMs: 35_000 }),
    ),
  getMcpTools: () =>
    generatedResult<{
      tools: Array<{
        name: string;
        title?: string;
        description?: string;
        readOnly?: boolean;
        inputSchema?: Record<string, unknown>;
      }>;
    }>(generatedMcp.listMcpTools()),

  // Blueprint API
  listBlueprints: () => generatedBlueprints.listBlueprints(),
  createBlueprint: (data: CreateBlueprintRequest) => generatedBlueprints.createBlueprint(data),
  getBlueprint: (id: string) => generatedBlueprints.getBlueprint(id),
  rebuildBlueprint: (id: string) => generatedBlueprints.rebuildBlueprint(id),
  deleteBlueprint: (id: string) => generatedBlueprints.deleteBlueprint(id),
  getBlueprintLogs: (id: string) => generatedBlueprints.getBlueprintLogs(id),

  // Devbox snapshot API (org-scoped, Runloop-backed like blueprints)
  listSnapshots: () => generatedSnapshots.listSnapshots(),
  createAgentSnapshot: (agentId: string, data: CreateSnapshotRequest) =>
    generatedAgents.createAgentSnapshot(agentId, data),
  getSnapshotStatus: (id: string) => generatedSnapshots.getSnapshotStatus(id),
  updateSnapshot: (id: string, data: UpdateSnapshotRequest) =>
    generatedSnapshots.updateSnapshot(id, data),
  deleteSnapshot: (id: string) => generatedSnapshots.deleteSnapshot(id),

  // Per-org base image (layered view: core + plugin contributions + org overrides).
  getBaseImage: (organizationId: string) => generatedOrganizations.getBaseImage(organizationId),
  updateBaseImageOverrides: (organizationId: string, data: UpdateBaseImageOverridesRequest) =>
    generatedOrganizations.setBaseImageOverrides(organizationId, data),
  rebuildBaseImage: (organizationId: string) =>
    generatedOrganizations.rebuildBaseImage(organizationId),

  getRecentActivity: (limit = 10) => generatedActivity.getRecentActivity({ limit }),

  getFlags: () => generatedFlags.listFeatureFlags(),
  setFlag: (key: string, enabled: boolean) => generatedFlags.setFeatureFlag(key, { enabled }),
  getAccountFlags: () => generatedFlags.listAccountFeatureFlags(),
  setAccountFlag: (key: string, enabled: boolean) =>
    generatedFlags.setAccountFeatureFlag(key, { enabled }),
  clearAccountFlag: (key: string) => generatedFlags.clearAccountFeatureFlag(key),
  getFlagOverrides: (key: string) => generatedFlags.getFeatureFlagOverrides(key),
  setFlagOverride: (key: string, userId: string, enabled: boolean) =>
    generatedFlags.setFeatureFlagOverride(key, userId, { enabled }),
  clearFlagOverride: (key: string, userId: string) =>
    generatedFlags.deleteFeatureFlagOverride(key, userId),

  getAuthSettings: () => generatedResult<AuthSettings>(generatedAuth.getAuthSettings()),
  updateAuthSettings: (
    allowedEmailDomains: string[],
    mode: AuthAllowlistMode = 'allow',
    allowedEmails: string[] = [],
    allowSelfServeOrgCreation?: boolean,
  ) =>
    generatedResult<AuthSettings>(
      generatedAuth.updateAuthSettings({
        // `allowSelfServeOrgCreation` is omitted when undefined so the server
        // preserves its current value (partial update semantics).
        allowedEmailDomains,
        allowedEmails,
        mode,
        ...(allowSelfServeOrgCreation !== undefined ? { allowSelfServeOrgCreation } : {}),
      }),
    ),

  getProfileStats: (userId?: string, year?: number) =>
    generatedProfile.getProfileStats({ userId, year }),

  logout: () =>
    generatedAuth.logout().then((res) => {
      clearAuthToken();
      return res;
    }),

  /**
   * Credential-less dev sign-in. Pass `email` to sign in as a specific
   * identity (honored only when the server runs with smoke-test hooks, e.g.
   * seeded previews); without it the server signs in the canonical John Doe.
   */
  devLogin: (email?: string) =>
    generatedResult<{ token: string; user: User }>(generatedAuth.devLogin({ email })).then(
      (res) => {
        setAuthToken(res.token);
        return res;
      },
    ),

  // ── Admin: service-level plugin configuration ──────────────────────
  getPluginServiceConfig: (pluginName: string) => generatedAdmin.getPluginServiceConfig(pluginName),

  updatePluginServiceConfig: (pluginName: string, values: PluginServiceConfigValues) =>
    generatedAdmin.updatePluginServiceConfig(pluginName, { values }),

  // User provider management
  getUserProviders: () =>
    generatedResult<
      Array<{
        id: string;
        provider: 'github' | 'google' | 'slack' | 'linear';
        providerUserId: string;
        providerData: Record<string, unknown> | null;
        createdAt: number;
        hasAccessToken: boolean;
      }>
    >(generatedUser.getMyProviders()),
  getUserProvidersById: (userId: string) =>
    generatedResult<
      Array<{
        id: string;
        provider: 'github' | 'google' | 'slack' | 'linear';
        providerUserId: string;
        providerData: Record<string, unknown> | null;
        createdAt: number;
        hasAccessToken: boolean;
      }>
    >(generatedUsers.getUserProviders(userId)),
  unlinkProvider: (provider: string) => generatedUser.disconnectMyProvider(provider),
  /**
   * Admin override of `unlinkProvider`: unlink ANY user's provider,
   * including their last one (no lockout guard). Gated on platform-admin.
   */
  adminUnlinkUserProvider: (userId: string, provider: string) =>
    generatedAdmin.deleteAdminUserProvider(userId, provider),
  /**
   * Identity-first sign-in surfaces every linked OAuth identity through
   * `auth_identities`. The Settings → Connected Accounts panel drives
   * off this endpoint (rather than `getUserProviders`, which is tied to
   * the OAuth-token store) so sign-in-only providers that don't keep a
   * stored token still appear in the list.
   */
  getUserIdentities: () =>
    generatedResult<{
      identities: Array<{
        id: string;
        provider: string;
        providerUserId: string;
        providerEmail: string | null;
        emailVerified: boolean;
        createdAt: number;
        lastUsedAt: number;
      }>;
    }>(generatedAuth.listAuthIdentities()),
  unlinkIdentity: (identityId: string) => generatedAuth.unlinkAuthIdentity(identityId),
  /**
   * Ask the `auth-email` plugin to mail a fresh verification link to the
   * signed-in password account. Plugin route (not in the generated
   * contract); the bearer identifies the caller.
   */
  requestEmailVerification: () =>
    request<EmailVerificationRequestStatus>('/auth/email/verify/request', { method: 'POST' }),
  // Onboarding state (PRD-11)
  getOnboardingState: () =>
    generatedResult<UserOnboardingStateView>(generatedUser.getOnboardingState()),
  skipOnboardingStep: (stepId: string) =>
    generatedResult<UserOnboardingStateView>(generatedUser.skipOnboardingStep(stepId)),
  dismissOnboardingBanner: () =>
    generatedResult<UserOnboardingStateView>(generatedUser.dismissOnboardingBanner()),
  dismissOnboardingWizard: () =>
    generatedResult<UserOnboardingStateView>(generatedUser.dismissOnboardingWizard()),

  // Secrets management
  getSecretsStatus: () => generatedAdmin.getAdminSecretsStatus(),
  /**
   * Fetch the secrets attachable to an agent env var. `owner` constrains
   * the result by the picker context: launch dialog and user-owned
   * personas pass the launching user (default); team-owned personas
   * pass the team; org-owned personas pass `{ ownerType: 'organization' }`.
   * The server validates the caller actually matches the owner before
   * fanning out the tier reads.
   */
  listAvailableSecrets: (
    nameQuery?: string,
    owner?: {
      ownerType: 'user' | 'team' | 'organization';
      ownerUserId?: string;
      ownerTeamId?: string;
    },
  ) => {
    return generatedSecrets.listAvailableSecrets({ name: nameQuery, ...owner });
  },
  setSecret: (name: string, value: string) => generatedAdmin.setAdminSecret(name, { value }),
  deleteSecret: (name: string) => generatedAdmin.deleteAdminSecret(name),

  // Service-tier secrets (super-admin only). The registry endpoint returns
  // the same `IntegrationSecretStatus[]` shape the main Secrets page uses,
  // so the polished `IntegrationSecretGroup` UI works against it directly —
  // no separate "what's set" lookup needed. The server enforces super-admin
  // in every verb.
  getServiceSecretRegistry: () =>
    generatedResult<IntegrationSecretStatus[]>(generatedAdmin.getServiceSecretRegistry()),
  setServiceSecret: (name: string, value: string) =>
    generatedAdmin.setServiceSecret(name, { value }),
  deleteServiceSecret: (name: string) => generatedAdmin.deleteServiceSecret(name),

  // Secret manager / rotation (super-admin only)
  getSecretManagerStatus: () =>
    generatedResult<SecretManagerStatus>(generatedAdmin.getSecretManagerStatus()),
  rotateSecrets: (batchSize?: number) =>
    generatedResult<SecretRotationResult>(generatedAdmin.rotateSecrets({ batchSize })),

  // Organizations & teams (RBAC)
  //
  // The server wraps list responses (`{ organizations }`, `{ members }`,
  // `{ teams }`, `{ roles }`, `{ team, members }`). Unwrap them here so
  // callers get a plain array / object to match what their UI expects —
  // otherwise `data.length` is `undefined` and lists silently render
  // empty even when the rows exist.
  listOrganizations: () =>
    generatedResult<{ organizations: AuthMembership[] }>(
      generatedOrganizations.listOrganizations(),
    ).then((r) => r.organizations),
  // Super-admin only: enumerate every organization in the system.
  // Backed by `GET /admin/organizations`, which 403s for non-super-admins.
  // Paginated + searchable — pass `{ page, pageSize, q }`. Defaults
  // mirror the server (`page: 1`, `pageSize: 20`) so callers that don't
  // care about pagination still get a sensible first page.
  listAllOrganizations: (opts: { page?: number; pageSize?: number; q?: string } = {}) =>
    generatedAdmin.listAdminOrganizations(opts),
  // Super-admin only: list all organizations a particular user belongs to.
  // Backed by `GET /admin/users/:userId/organizations`. Returns the same
  // membership shape as `GET /organizations` (org + roles + permissions) so
  // the admin user detail page can render rich rows.
  listAdminUserOrganizations: (userId: string) =>
    generatedResult<{ organizations: AuthMembership[] }>(
      generatedAdmin.getAdminUserOrganizations(userId),
    ).then((r) => r.organizations),
  getOrganization: (id: string) =>
    generatedResult<AuthMembership['organization']>(generatedOrganizations.getOrganization(id)),
  // Super-admin only: at-a-glance activity counts for an org over the last
  // ~7 days (agent sessions, sign-ins, personas, flows). Backs the admin org
  // overview "Recent activity" panel. GET /admin/organizations/:id/activity.
  getOrganizationActivity: (id: string) => generatedAdmin.getAdminOrganizationActivity(id),
  createOrganization: (data: {
    companyName?: string;
    companySlug?: string;
    name: string;
    slug?: string;
    supportEmail?: string;
    emailDomains?: string[];
    pluginNames?: string[];
    orgOnboardingProfile?: OrgOnboardingProfile;
  }) =>
    generatedResult<AuthMembership['organization']>(
      generatedOrganizations.createOrganization(data),
    ),
  // Super-admin only: create an organization on behalf of another user. The
  // requested `ownerUserId` receives the `org:owner` role; the requesting
  // admin is recorded as `createdByUserId` for audit but is not added as a
  // member of the new org.
  // Owner is either an existing `ownerUserId` or a `pendingOwner` the server
  // pre-provisions by email atomically with the org (for someone who has never
  // signed in). Send exactly one; a taken email 409s with
  // `code: 'email_already_registered'`.
  adminCreateOrganization: (data: {
    name: string;
    slug?: string;
    ownerUserId?: string;
    pendingOwner?: { email: string; name?: string };
    avatarUrl?: string;
    supportEmail?: string;
    emailDomains?: string[];
    pluginNames?: string[];
    orgOnboardingProfile?: OrgOnboardingProfile;
    capabilities?: string[];
    subscription?: { type?: 'trial'; durationDays?: number };
  }) => generatedAdmin.createAdminOrganization(data),
  // Platform-admin empty-org flow. The browser supplies only a durable
  // operation id; the server resolves its own Runloop service credential,
  // mints the account, saves the org-scoped key, and leaves setup incomplete.
  adminCreateProvisionedOrganization: (data: {
    idempotencyKey: string;
    name: string;
    slug?: string;
    ownerUserId?: string;
    pendingOwner?: { email: string; name?: string };
    avatarUrl?: string;
    supportEmail?: string;
    emailDomains?: string[];
    pluginNames?: string[];
    orgOnboardingProfile?: OrgOnboardingProfile;
    capabilities?: string[];
    subscription?: { type?: 'trial'; durationDays?: number };
  }) => generatedAdmin.createProvisionedAdminOrganization(data),
  updateOrganization: (
    id: string,
    data: {
      name?: string;
      slug?: string;
      avatarUrl?: string | null;
      supportEmail?: string | null;
      capabilities?: string[];
      enabledReleaseChannels?: Array<'preview' | 'alpha' | 'beta'>;
      sandboxRetentionDays?: number | null;
      defaultSandboxSize?: string | null;
      customSandboxSizes?: CustomSandboxSize[];
      sandboxArtifactMirrors?: boolean;
      allowSessionShareView?: boolean;
      allowSessionShareEdit?: boolean;
    },
  ) =>
    generatedResult<AuthMembership['organization']>(
      generatedOrganizations.updateOrganization(id, data),
    ),
  // Platform-admin-only atomic capability mutation. Admin surfaces use
  // this instead of smuggling capability changes through the org-scoped
  // settings endpoint. (The legacy whole-array route still exists server-side
  // for backwards compatibility, but no web surface calls it.)
  setAdminOrganizationCapability: (id: string, capability: string, enabled: boolean) =>
    generatedResult<AuthMembership['organization']>(
      generatedAdmin.setAdminOrganizationCapability(id, { capability, enabled }),
    ),
  setAdminOrganizationSubscriptionTier: (
    id: string,
    subscriptionTier: OrganizationSubscriptionTier,
  ) =>
    generatedResult<AuthMembership['organization']>(
      generatedAdmin.setAdminOrganizationSubscriptionTier(id, { subscriptionTier }),
    ),
  getAdminEnterpriseIdentityStatus: (id: string) =>
    generatedAdmin.getAdminEnterpriseIdentityStatus(id),
  setAdminOrganizationBillingEntity: (
    id: string,
    input: { billingEntityId: string; reason: string; acknowledgeSsoReset?: boolean },
  ) =>
    generatedResult<AdminOrganizationBillingEntityMoveResult>(
      generatedAdmin.setAdminOrganizationBillingEntity(id, input),
    ),
  listAdminBillingEntities: () => generatedAdmin.listAdminBillingEntities(),
  createAdminBillingEntity: (name: string, subscriptionTier: OrganizationSubscriptionTier) =>
    generatedAdmin.createAdminBillingEntity({ name, subscriptionTier }),
  // Re-typed to the shared contract: OpenAPI cannot express the company id's
  // prefix, so the generated model widens it to `string` while every consumer
  // works with the shared `BillingEntity`. Same wire shape either way.
  getAdminOrganizationBillingEntity: (id: string) =>
    generatedResult<AdminOrganizationBillingEntity>(
      generatedAdmin.getAdminOrganizationBillingEntity(id),
    ),
  updateAdminBillingEntity: (
    billingEntityId: string,
    patch: { name?: string; subscriptionTier?: OrganizationSubscriptionTier },
  ) => generatedAdmin.updateAdminBillingEntity(billingEntityId, patch),
  listAdminCompanyDomainClaims: (billingEntityId: string) =>
    generatedResult<{ claims: BillingEntityDomainClaim[] }>(
      generatedAdmin.listAdminEmailDomains({ billingEntityId }),
    ).then((result) => result.claims),
  removeAdminCompanyDomainClaim: (domain: string, reason: string) =>
    generatedAdmin.deleteAdminEmailDomain(domain, { reason }),
  reassignAdminCompanyDomainClaim: (domain: string, billingEntityId: string, reason: string) =>
    generatedResult<{ claim: BillingEntityDomainClaim; previousBillingEntityId: string }>(
      generatedAdmin.reassignAdminEmailDomain(domain, { billingEntityId, reason }),
    ),
  getAdminBillingEntity: (billingEntityId: string) =>
    generatedAdmin.getAdminBillingEntity(billingEntityId),
  createAdminBillingEntityDomainClaim: (
    billingEntityId: string,
    input: { domain: string; reason: string },
  ) => generatedAdmin.createAdminBillingEntityDomainClaim(billingEntityId, input),
  verifyAdminBillingEntityDomainClaim: (billingEntityId: string, claimId: string) =>
    generatedAdmin.verifyAdminBillingEntityDomainClaim(billingEntityId, claimId),
  removeAdminBillingEntityDomainClaim: (billingEntityId: string, claimId: string, reason: string) =>
    generatedAdmin.removeAdminBillingEntityDomainClaim(billingEntityId, claimId, { reason }),
  approveAdminBillingEntityDomainClaim: (
    billingEntityId: string,
    claimId: string,
    reason: string,
  ) => generatedAdmin.approveAdminBillingEntityDomainClaim(billingEntityId, claimId, { reason }),
  updateAdminBillingEntityDomainClaimPolicy: (
    billingEntityId: string,
    claimId: string,
    patch: { ssoDiscoveryEnabled?: boolean; autoJoinOrganizationId?: string | null },
    reason: string,
  ) =>
    generatedAdmin.updateAdminBillingEntityDomainClaimPolicy(billingEntityId, claimId, {
      ...patch,
      reason,
    }),
  listOrganizationEmailDomains: (id: string) =>
    generatedResult<{
      domains: Array<{
        id: string;
        organizationId: string;
        domain: string;
        createdAt: number;
        createdByUserId: string | null;
      }>;
    }>(generatedOrganizations.listOrgEmailDomains(id)).then((r) => r.domains),
  addOrganizationEmailDomain: (id: string, domain: string) =>
    generatedResult<{
      id: string;
      organizationId: string;
      domain: string;
      createdAt: number;
      createdByUserId: string | null;
    }>(generatedOrganizations.addOrgEmailDomain(id, { domain })),
  removeOrganizationEmailDomain: (id: string, domain: string) =>
    generatedOrganizations.removeOrgEmailDomain(id, domain),
  listOrganizationMembers: (id: string) =>
    generatedResult<{
      members: Array<{
        userId: string;
        memberId: string;
        roles: AuthMembership['roles'];
      }>;
    }>(generatedOrganizations.listOrgMembers(id)).then((r) => r.members),
  addOrganizationMember: (id: string, userId: string) =>
    generatedResult<{ memberId: string }>(generatedOrganizations.addOrgMember(id, { userId })),
  removeOrganizationMember: (id: string, userId: string) =>
    generatedOrganizations.removeOrgMember(id, userId),
  createOrganizationInvite: (id: string, email: string, roleId?: string) =>
    generatedResult<CreateOrganizationInviteResponse>(
      generatedOrganizations.createOrgInvite(id, { email, ...(roleId ? { roleId } : {}) }),
    ),
  listOrganizationInvites: (id: string, opts: { status?: OrganizationInviteListFilter } = {}) => {
    return generatedResult<{ invites: OrganizationInvite[] }>(
      generatedOrganizations.listOrgInvites(id, opts),
    ).then((r) => r.invites);
  },
  /**
   * Re-issues an invite's redeem link. The link shared before this call
   * stops working, and the replacement comes back in plaintext exactly
   * once, same as {@link createOrganizationInvite}.
   */
  resendOrganizationInvite: (id: string, inviteId: string) =>
    generatedResult<CreateOrganizationInviteResponse>(
      generatedOrganizations.resendOrgInvite(id, inviteId),
    ),
  revokeOrganizationInvite: (id: string, inviteId: string) =>
    generatedResult<OrganizationInvite>(generatedOrganizations.revokeOrgInvite(id, inviteId)),
  /** Public landing-page lookup; no auth required. */
  getInviteLanding: (token: string) =>
    generatedResult<InviteLandingResponse>(generatedInvites.getInviteLanding(token)),
  /** Lists pending invites addressed to the signed-in user's email. */
  listPendingInvites: () =>
    generatedResult<ListPendingInvitesResponse>(generatedInvites.listPendingInvites()).then(
      (r) => r.invites,
    ),
  /** Redeems every pending invite for the signed-in user's email. */
  acceptPendingInvites: () =>
    generatedResult<AcceptPendingInvitesResponse>(generatedInvites.acceptPendingInvites()),
  /** Redeems only the explicitly selected invite for the signed-in user's email. */
  acceptInvite: (inviteId: string) =>
    generatedResult<AcceptInviteResponse>(generatedInvites.acceptInvite(inviteId)),
  /**
   * Declines a single pending invite by id. One-time and terminal: the
   * invite drops out of the pending list and rejoining needs a fresh
   * admin invite. Email-gated server-side so a caller can only decline
   * invites addressed to their own signed-in email.
   */
  declineInvite: (inviteId: string) => generatedInvites.declineInvite(inviteId),
  assignOrganizationRole: (id: string, userId: string, roleId: string) =>
    generatedResult<{ ok: boolean }>(generatedOrganizations.assignOrgRole(id, { userId, roleId })),
  revokeOrganizationRole: (id: string, userId: string, roleId: string) =>
    generatedResult<{ ok: boolean }>(generatedOrganizations.revokeOrgRole(id, { userId, roleId })),
  listOrganizationRoles: (id: string) =>
    generatedResult<{ roles: AuthMembership['roles'] }>(
      generatedOrganizations.listOrgRoles(id),
    ).then((r) => r.roles),
  getOrganizationRole: (id: string, roleId: string) =>
    generatedResult<AuthMembership['roles'][number]>(generatedOrganizations.getOrgRole(id, roleId)),
  updateOrganizationRole: (
    id: string,
    roleId: string,
    data: { name?: string; description?: string | null; permissions?: string[] },
  ) =>
    generatedResult<AuthMembership['roles'][number]>(
      generatedOrganizations.updateOrgRole(id, roleId, data),
    ),
  /**
   * Runtime permission catalog. Includes the host base plus every
   * plugin-contributed entry; the role-permission picker iterates this
   * list rather than the static `PERMISSION_CATALOG` so plugin slugs
   * (e.g. `projects:read`) show up when their plugin is loaded.
   */
  getPermissionsCatalog: () =>
    generatedResult<{
      permissions: Array<{ slug: string; category: string; description: string }>;
    }>(generatedPermissions.getPermissionsCatalog()).then((r) => r.permissions),
  listTeams: (organizationId: string) =>
    generatedResult<{
      teams: Array<{
        id: string;
        organizationId: string;
        slug: string;
        name: string;
        description: string | null;
        createdAt: number;
        memberCount: number;
      }>;
    }>(generatedOrganizations.listOrgTeams(organizationId)).then((r) => coerceArray(r?.teams)),
  createTeam: (
    organizationId: string,
    data: { name: string; slug?: string; description?: string | null },
  ) =>
    generatedResult<{
      id: string;
      organizationId: string;
      slug: string;
      name: string;
      description: string | null;
      createdAt: number;
    }>(generatedOrganizations.createOrgTeam(organizationId, data)),
  getTeam: (teamId: string) =>
    generatedResult<{
      team: {
        id: string;
        organizationId: string;
        slug: string;
        name: string;
        description: string | null;
        defaultRoleId: string | null;
        createdAt: number;
      };
      members: Array<{
        userId: string;
        memberId: string;
        role: AuthMembership['roles'][number] | null;
      }>;
    }>(generatedTeams.getTeam(teamId)).then((r) => r.team),
  updateTeam: (
    teamId: string,
    data: {
      name?: string;
      slug?: string;
      description?: string | null;
      defaultRoleId?: string;
    },
  ) =>
    generatedResult<{
      id: string;
      organizationId: string;
      slug: string;
      name: string;
      description: string | null;
      defaultRoleId: string | null;
      createdAt: number;
    }>(generatedTeams.updateTeam(teamId, data)),
  deleteTeam: (teamId: string) => generatedTeams.deleteTeam(teamId),
  listTeamMembers: (teamId: string) =>
    generatedResult<{
      members: Array<{
        userId: string;
        memberId: string;
        role: AuthMembership['roles'][number] | null;
      }>;
    }>(generatedTeams.listTeamMembers(teamId)).then((r) => r.members),
  addTeamMember: (teamId: string, userId: string) =>
    generatedResult<{ memberId: string }>(generatedTeams.addTeamMember(teamId, { userId })),
  removeTeamMember: (teamId: string, userId: string) =>
    generatedTeams.removeTeamMember(teamId, userId),
  setTeamMemberRole: (teamId: string, userId: string, roleId: string) =>
    generatedTeams.setTeamMemberRole(teamId, userId, { roleId }),

  listTeamSecrets: (organizationId: string, teamId: string) =>
    generatedOrganizations.listTeamSecrets(organizationId, teamId).then((r) => r.secrets),
  createTeamSecret: (organizationId: string, teamId: string, data: CreateScopedSecretRequest) =>
    generatedOrganizations.createTeamSecret(organizationId, teamId, data),
  updateTeamSecret: (
    organizationId: string,
    teamId: string,
    secretId: string,
    data: UpdateScopedSecretRequest,
  ) => generatedOrganizations.updateTeamSecret(organizationId, teamId, secretId, data),
  deleteTeamSecret: (organizationId: string, teamId: string, secretId: string) =>
    generatedOrganizations.deleteTeamSecret(organizationId, teamId, secretId),

  listUserSecrets: () =>
    generatedResult<ScopedSecretsListResponse>(generatedMe.listMySecrets()).then((r) => r.secrets),
  createUserSecret: (data: CreateScopedSecretRequest) =>
    generatedResult<ScopedSecret>(generatedMe.createMySecret(data)),
  updateUserSecret: (secretId: string, data: UpdateScopedSecretRequest) =>
    generatedResult<ScopedSecret>(generatedMe.updateMySecret(secretId, data)),
  deleteUserSecret: (secretId: string) => generatedMe.deleteMySecret(secretId),

  // ── Model-provider secrets (mps_*) ───────────────────────────────
  //
  // The wrapper rows minted from provider API keys / subscription
  // credentials. Three URL shapes share one shape — same as the
  // generic scoped-secrets surface above — and the URL the client
  // hits decides which `(scope, ownerId)` tuple the row lands under.
  // Server is the source of truth for the `default` wrapper-name
  // convention; callers pass `name: 'default'` for the "this is my
  // key for that provider" slot.
  listMyModelProviderSecrets: () =>
    generatedResult<ModelProviderSecretsListResponse>(
      generatedMe.listMyModelProviderSecrets(),
    ).then((r) => r.secrets),
  createMyModelProviderSecret: (data: CreateModelProviderSecretRequest) =>
    generatedResult<ModelProviderSecret>(generatedMe.createMyModelProviderSecret(data)),
  updateMyModelProviderSecret: (secretId: string, data: UpdateModelProviderSecretRequest) =>
    generatedResult<ModelProviderSecret>(generatedMe.updateMyModelProviderSecret(secretId, data)),
  deleteMyModelProviderSecret: (secretId: string) =>
    generatedMe.deleteMyModelProviderSecret(secretId),

  // Every model-provider secret the caller can *use* (user + team + org
  // tiers folded, precedence-sorted), optionally narrowed to a set of
  // types. Powers the launch + chat key pickers, which must surface keys
  // a member can use even when they can't administer them.
  listAccessibleModelProviderSecrets: (types?: ModelProviderSecretType[]) => {
    return generatedMe
      .listAccessibleModelProviderSecrets({ types: types?.join(',') })
      .then((r) => r.secrets);
  },

  listOrgModelProviderSecrets: (organizationId: string) =>
    generatedResult<ModelProviderSecretsListResponse>(
      generatedOrganizations.listOrgModelProviderSecrets(organizationId),
    ).then((r) => r.secrets),
  createOrgModelProviderSecret: (organizationId: string, data: CreateModelProviderSecretRequest) =>
    generatedResult<ModelProviderSecret>(
      generatedOrganizations.createOrgModelProviderSecret(organizationId, data),
    ),
  updateOrgModelProviderSecret: (
    organizationId: string,
    secretId: string,
    data: UpdateModelProviderSecretRequest,
  ) =>
    generatedResult<ModelProviderSecret>(
      generatedOrganizations.updateOrgModelProviderSecret(organizationId, secretId, data),
    ),
  deleteOrgModelProviderSecret: (organizationId: string, secretId: string) =>
    generatedOrganizations.deleteOrgModelProviderSecret(organizationId, secretId),
  listOrgModelProviderSecretDependents: (organizationId: string, secretId: string) =>
    generatedResult<ResourceDependentsResponse>(
      generatedOrganizations.listOrgModelProviderSecretDependents(organizationId, secretId),
    ),

  listLlmTaskAssignments: (organizationId: string) =>
    generatedOrganizations.listLlmTaskAssignments(organizationId).then((r) => r.assignments),
  upsertLlmTaskAssignment: (
    organizationId: string,
    taskId: LlmTaskId,
    data: UpsertLlmTaskAssignmentRequest,
  ) => generatedOrganizations.upsertLlmTaskAssignment(organizationId, taskId, data),

  listTeamModelProviderSecrets: (organizationId: string, teamId: string) =>
    generatedResult<ModelProviderSecretsListResponse>(
      generatedOrganizations.listTeamModelProviderSecrets(organizationId, teamId),
    ).then((r) => r.secrets),
  createTeamModelProviderSecret: (
    organizationId: string,
    teamId: string,
    data: CreateModelProviderSecretRequest,
  ) =>
    generatedResult<ModelProviderSecret>(
      generatedOrganizations.createTeamModelProviderSecret(organizationId, teamId, data),
    ),
  updateTeamModelProviderSecret: (
    organizationId: string,
    teamId: string,
    secretId: string,
    data: UpdateModelProviderSecretRequest,
  ) =>
    generatedResult<ModelProviderSecret>(
      generatedOrganizations.updateTeamModelProviderSecret(organizationId, teamId, secretId, data),
    ),
  deleteTeamModelProviderSecret: (organizationId: string, teamId: string, secretId: string) =>
    generatedOrganizations.deleteTeamModelProviderSecret(organizationId, teamId, secretId),

  // Agent model policy (organization scoped)
  getAgentModelPolicy: () => generatedAdmin.getAdminAgentModels(),
  toggleAgentProvider: (agentType: string, providerId: string, enabled: boolean, scope: 'org') =>
    generatedAdmin.setAgentModelProviderAvailability(agentType, providerId, { enabled, scope }),
  toggleAgentModel: (agentType: string, modelId: string, enabled: boolean, scope: 'org') =>
    generatedAdmin.setAgentModelAvailability(agentType, modelId, { enabled, scope }),
  setAgentModelDefault: (
    agentType: string,
    defaults: { modelId?: string; providerId?: string },
    scope: 'org',
  ) => generatedAdmin.setAgentModelDefault(agentType, { ...defaults, scope }),
  setLaunchDefaultAgent: (params: { scope: 'org'; agentType?: string }) =>
    generatedAdmin.setLaunchDefaultAgent(params),
  setAgentTypeEnabled: (agentType: string, enabled: boolean, scope: 'org') =>
    generatedAdmin.setAgentTypeEnabled(agentType, { enabled, scope }),

  // User credentials (claude-max, etc.) — /me/credentials/*
  //
  // User-tier credentials are personal credentials registered by individual
  // developers, isolated per user, and never visible to org admins. The first
  // implementation is Claude Max.
  getUserCredentials: () =>
    generatedResult<UserCredentialsListResponse>(generatedMe.listMyCredentials()),
  getClaudeMaxStatus: () =>
    generatedResult<{ configured: boolean; kind: string; alias: string }>(
      generatedMe.getMyCredentialStatus('claude-max'),
    ),
  registerClaudeMax: (blob: ClaudeMaxCredential, alias?: string) =>
    generatedMe.registerClaudeMaxCredential({ blob, alias }),
  getCodexSubscriptionStatus: () =>
    generatedResult<CredentialStatusResponse>(
      generatedMe.getMyCredentialStatus('codex-subscription'),
    ),
  registerCodexSubscription: (blob: CodexSubscriptionCredential, alias?: string) =>
    generatedMe.registerCodexSubscriptionCredential({ blob, alias }),
  deleteCodexSubscription: (alias?: string) =>
    generatedMe.deleteMyCredential('codex-subscription', { alias }),
  deleteClaudeMax: (alias?: string) => generatedMe.deleteMyCredential('claude-max', { alias }),

  // Per-org plugin installations
  //
  // The catalog is org-agnostic and used by the create-organization dialog
  // and setup wizard before an org id exists. Per-org reads/writes go
  // through `/organizations/:id/plugins/...`.
  getPluginCatalog: () =>
    generatedResult<PluginCatalogResponse>(generatedPlugins.getPluginsCatalog()),
  getOrgPlugins: (organizationId: string) =>
    generatedResult<OrgPluginListResponse>(generatedOrganizations.listOrgPlugins(organizationId)),
  getOrgPlugin: (organizationId: string, pluginName: string) =>
    generatedResult<InstallationView>(
      generatedOrganizations.getOrgPlugin(organizationId, pluginName),
    ),
  getOrgPluginUninstallPreview: (organizationId: string, pluginName: string) =>
    generatedResult<OrgPluginUninstallPreview>(
      generatedOrganizations.previewOrgPluginUninstall(organizationId, pluginName),
    ),
  installOrgPlugin: (organizationId: string, pluginName: string) =>
    generatedResult<InstallationView>(
      generatedOrganizations.installOrgPlugin(organizationId, pluginName, {
        timeoutMs: 90_000,
      }),
    ),
  uninstallOrgPlugin: (organizationId: string, pluginName: string) =>
    generatedResult<{ target: InstallationView; cascaded: InstallationView[] }>(
      generatedOrganizations.uninstallOrgPlugin(organizationId, pluginName, { timeoutMs: 90_000 }),
    ),

  // Per-org plugin settings (declared via `PluginServerDefinition.orgSettings`).
  // Reads are member-tier (`plugins:read`); writes are admin-tier
  // (`plugins:manage`).
  getOrgPluginSettings: (organizationId: string, pluginName: string) =>
    generatedResult<OrgPluginSettingsView>(
      generatedOrganizations.getOrgPluginSettings(organizationId, pluginName),
    ),
  updateOrgPluginSettings: (
    organizationId: string,
    pluginName: string,
    values: OrgPluginSettingsValues,
  ) =>
    generatedResult<OrgPluginSettingsView>(
      generatedOrganizations.setOrgPluginSettings(organizationId, pluginName, {
        values,
      }),
    ),
};
